import { useFormContext } from "react-hook-form";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { PatientOrderPermission } from "@/components/studentApplication/PatientOrderPermission";
import {
  DocumentProof,
  LevelFeatures
} from "@/components/studentApplication/sections/EducationalInstitution";
import { DOCUMENT_COVERING_LETTER, DOCUMENT_STUDENT_ID } from "@/core/constants";
import { SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY } from "@/graphql/accounts";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import FormControl from "@/tpo/FormControl";
import { Error } from "@/tpo/InputWrapper";
import Stack from "@/tpo/Stack";
import PractitionerTypeField from "@/tpo/partnerDashboard/practitioner/Application/ui/PractitionerTypeField";
import QualificationProof from "@/tpo/partnerDashboard/practitioner/Application/ui/QualificationProof";
import { get } from "@/utils/nodash";
import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

function FullPractitionerFields() {
  const { data: { practitionerTypes = [] } = {} } = useQuery(
    SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY
  );

  const { setValue, clearErrors } = useFormContext();

  return (
    <>
      <FormControl
        label={
          <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
            What is your profession?
          </Box>
        }
        spaceAboveHelpText={2}
        helpText={
          <Box fontFamily="gilroyMedium" fontSize={16}>
            Select your primary professional focus
          </Box>
        }
        spaceAboveChildren={[20, 20, 40]}
      >
        <Stack alignItems="flex-start" gap={[20, 20, 40]}>
          <PractitionerTypeField
            practitionerTypes={practitionerTypes}
            onChange={val => {
              setValue("practitionerType", val);
              setValue("documents", []);
              clearErrors(["practitionerType", "documents"]);
            }}
          />
        </Stack>
      </FormControl>
      <QualificationProof practitionerTypes={practitionerTypes} />
    </>
  );
}

function StudentPractitionerFields({ currentLevel, patientOrderPermission }) {
  const {
    formState: { errors },
    watch
  } = useFormContext();

  const documentError = get(errors, "documents")?.message;

  const levelValue = watch("level");

  return (
    <>
      <LevelFeatures variant="upgrade" />
      <Stack gap={40}>
        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[28]}>
            Proof of Enrollment
          </Box>
          <Box>
            Please upload a valid proof of enrolment – student ID AND confirmation letter of current
            level from your colleges. We will require this before we can complete your approval to
            the platform.
          </Box>
        </Stack>
        <DocumentProof
          disabled={levelValue === currentLevel}
          label="Student ID"
          name={DOCUMENT_STUDENT_ID}
        />
        <DocumentProof
          disabled={levelValue === currentLevel}
          label="Confirmation letter"
          name={DOCUMENT_COVERING_LETTER}
        />
        {documentError && <Error error={documentError} />}

        {levelValue === "Level 6" && (
          <PatientOrderPermission previouslyGranted={patientOrderPermission} />
        )}
      </Stack>
    </>
  );
}

export default function StudentPractitionerUpgrade({
  children,
  currentLevel,
  patientOrderPermission
}) {
  const theme = useTheme();

  const { watch } = useFormContext();

  const levelValue = watch("level");

  return (
    <Box bg="white" pt={theme.spacing.section.pt} pb={theme.spacing.section.pb}>
      <Stack bg="white" width={720} mx="auto" gap={40}>
        <ControlledRadioGroup
          Container={Stack}
          containerProps={{
            gap: 16
          }}
          name="level"
          label={
            <>
              <Box fontFamily="gilroyBold" lineHeight={1.5} fontSize={36} mb={40}>
                Upgrade your study level
              </Box>
              <Box fontFamily="gilroyMedium" lineHeight={1.5} fontSize={16} mb={24}>
                If you have completed your previous year and have moved to the next level, apply
                below to upgrade your Regenerus student access
              </Box>
              <Box fontFamily="gilroyMedium" lineHeight={["24px"]} fontSize={16} mb={40}>
                View qualification level definitions
              </Box>
              <Box fontFamily="gilroyMedium" lineHeight={["28px"]} fontSize={18} mb={16}>
                Select new level
              </Box>
            </>
          }
          labelProps={{
            fontFamily: "gilroyBold",
            fontSize: 18,
            mb: 20
          }}
          values={[
            {
              label: "Level 4",
              value: "Level 4",
              disabled: true
            },
            {
              label: "Level 5",
              value: "Level 5",
              disabled:
                currentLevel == "Level 5" ||
                currentLevel == "Level 6 ION" ||
                currentLevel == "Level 6" ||
                currentLevel == "Full access"
            },
            {
              label: "Level 6",
              value: "Level 6",
              disabled:
                currentLevel == "Level 6" ||
                currentLevel == "Level 6 ION" ||
                currentLevel == "Full access"
            },
            {
              label: "Full practitioner access",
              value: "Full access",
              disabled: false
            }
          ]}
        />
        {levelValue && levelValue !== currentLevel && (
          <>
            <Divider />
            {levelValue === "Full access" ? (
              <FullPractitionerFields />
            ) : (
              <StudentPractitionerFields
                currentLevel={currentLevel}
                patientOrderPermission={patientOrderPermission}
              />
            )}
            {children}
          </>
        )}
      </Stack>
    </Box>
  );
}
